/* ---------------------------------------------------------------------------
 * cc-cart.js — behaviour of the site cart drawer (sections/cc-cart-drawer.liquid,
 * snippets/cc-cart-drawer.liquid). Contract: docs/gifts-bundles.md §4.
 *
 * Backed by the real Shopify cart (Ajax API): GET /cart.js, POST /cart/add.js,
 * /cart/change.js, /cart/update.js. Requests are serialised so two quick "+"
 * clicks can never race.
 *
 * PUBLIC API — for gft-grid, bnd-hero and anything else that adds to the cart:
 *
 *   window.CCCart.add(items, { open = true, keepMarkers = false })  -> Promise<cart>
 *       items: [{ id: <variantId>, quantity: 1, properties?: {...} }]
 *       POSTs /cart/add.js (ACCUMULATES — never clears), re-reads /cart.js,
 *       REMOVES any ONETIMEORDER/ORDERGUARANTEE marker lines (shared rule, see
 *       stripMarkers below), re-renders, updates every count badge and (by
 *       default) opens the drawer.
 *       Rejects with an Error whose .message is Shopify's `description`
 *       (e.g. sold out) — the caller shows it; the drawer is left closed.
 *   window.CCCart.open()      -> Promise<cart>   (re-reads the cart first)
 *   window.CCCart.close()
 *   window.CCCart.refresh()   -> Promise<cart>   (re-read + re-render, no open)
 *   window.CCCart.cart        last /cart.js payload (or null)
 *
 *   Events on `document`:
 *     listen  'cc:cart:open'     (detail ignored)  → open()
 *     listen  'cc:cart:close'                      → close()
 *     listen  'cc:cart:refresh'                    → refresh()
 *     emit    'cc:cart:updated'  detail { cart, count, lines }
 *             count/lines EXCLUDE the hidden marker lines.
 *   Any element with [data-cc-cart-open] opens the drawer on click (the header
 *   button); [data-cc-cart-close] closes it.
 *
 *   Because this file is loaded with `defer`, a section script that runs before
 *   it should call `window.CCCart ? CCCart.add(...) : …` or simply POST
 *   /cart/add.js itself and then dispatch `cc:cart:open` — both are supported.
 *
 * WHAT IT DOES NOT DO: it never patches fetch/XHR and never listens to other
 * forms, so the phase-1 PDP add-to-cart (clear → add → location = /checkout)
 * is untouched.
 *
 * MARKER LINES: $0 "First Order Guarantee" lines (SKUs / handles in
 * snippets/cc-cart-markers.liquid, passed as data attributes) are left in the
 * cart but never listed or counted. If a shopper removes the last visible line
 * FROM THE DRAWER and only marker lines remain, the markers are removed too, so
 * an "empty" cart is really empty (an orphan marker would otherwise turn the
 * next bundle checkout into a subscription first-order).
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (window.CCCart) return;

  var root = document.getElementById('CcCart');
  if (!root) return;

  var panel = root.querySelector('.cc-cart__panel');
  var linesEl = root.querySelector('[data-cc-cart-lines]');
  var emptyEl = root.querySelector('[data-cc-cart-empty]');
  var errEl = root.querySelector('[data-cc-cart-error]');
  var subtotalEl = root.querySelector('[data-cc-cart-subtotal]');
  var checkoutEl = root.querySelector('[data-cc-cart-checkout]');
  var xBlock = root.querySelector('[data-cc-xsell-block]');
  var xItems = root.querySelectorAll('[data-cc-xsell]');

  function list(attr) {
    return (root.getAttribute(attr) || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean);
  }
  var hiddenSkus = list('data-hidden-skus');
  var hiddenHandles = list('data-hidden-handles');
  var typeLabels = {};
  (root.getAttribute('data-type-labels') || '').split('|').forEach(function (pair) {
    var i = pair.indexOf(':');
    if (i > 0) typeLabels[pair.slice(0, i).trim().toLowerCase()] = pair.slice(i + 1).trim();
  });

  var state = { cart: null, open: false, lastFocus: null };

  /* ---------- request queue ---------- */
  var chain = Promise.resolve();
  function enqueue(fn) {
    var p = chain.then(fn);
    chain = p.catch(function () {});
    return p;
  }
  function req(url, body) {
    var opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body) {
      opts.method = 'POST';
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(url, opts).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok) {
          var e = new Error((j && (j.description || j.message)) || 'Something went wrong. Please try again.');
          e.status = r.status;
          throw e;
        }
        return j;
      });
    });
  }
  function root_url(path) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base.replace(/\/$/, '') + path;
  }
  function getCart() { return req(root_url('/cart.js')); }

  /* ---------- helpers ---------- */
  function isHidden(item) {
    return hiddenSkus.indexOf(item.sku || '') > -1 || hiddenHandles.indexOf(item.handle || '') > -1;
  }
  function money(cents, currency) {
    var n = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }
  function sized(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function subLabel(item) {
    var parts = [];
    if (item.gift_card) {
      if (item.variant_title) parts.push(item.variant_title);
      var p = item.properties || {};
      var to = p['Recipient name'] || p['Recipient Name'];
      if (to) parts.push('for ' + to);
      return parts.join(' · ');
    }
    /* The design's sub-line is the product's kicker ("Meal bundle", "Merch"),
       i.e. its type — not the variant title (meal variants carry internal
       titles such as "Category / Curries"). */
    var t = (item.product_type || '').trim();
    if (t) parts.push(typeLabels[t.toLowerCase()] || t);
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      parts.push(item.selling_plan_allocation.selling_plan.name);
    }
    return parts.join(' · ');
  }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function showError(msg) {
    if (!errEl) return;
    errEl.textContent = msg || '';
    errEl.hidden = !msg;
  }

  /* ---------- render ---------- */
  function render(cart) {
    state.cart = cart;
    var visible = (cart.items || []).filter(function (i) { return !isHidden(i); });
    var count = visible.reduce(function (n, i) { return n + i.quantity; }, 0);
    var subtotal = visible.reduce(function (n, i) { return n + (i.final_line_price || 0); }, 0);

    linesEl.textContent = '';
    visible.forEach(function (item) {
      var row = el('div', 'cc-cart__line');
      row.setAttribute('data-key', item.key);

      var thumb = el('div', 'cc-cart__thumb');
      var src = item.image || (item.featured_image && item.featured_image.url);
      if (src) {
        var img = el('img');
        img.src = sized(src, 136);
        img.alt = '';
        img.width = 68;
        img.height = 68;
        img.loading = 'lazy';
        thumb.appendChild(img);
      }
      row.appendChild(thumb);

      var info = el('div', 'cc-cart__info');
      info.appendChild(el('span', 'cc-cart__title', item.product_title || item.title));
      var sub = subLabel(item);
      if (sub) info.appendChild(el('span', 'cc-cart__sub', sub));
      var qty = el('div', 'cc-cart__qty');
      var dec = el('button', 'cc-cart__qbtn', '−');
      dec.type = 'button';
      dec.setAttribute('aria-label', 'Decrease quantity');
      dec.setAttribute('data-cc-qty', '-1');
      var q = el('span', null, String(item.quantity));
      var inc = el('button', 'cc-cart__qbtn', '+');
      inc.type = 'button';
      inc.setAttribute('aria-label', 'Increase quantity');
      inc.setAttribute('data-cc-qty', '1');
      qty.appendChild(dec);
      qty.appendChild(q);
      qty.appendChild(inc);
      info.appendChild(qty);
      row.appendChild(info);

      row.appendChild(el('span', 'cc-cart__total', money(item.final_line_price, cart.currency)));
      linesEl.appendChild(row);
    });

    emptyEl.hidden = count > 0;
    subtotalEl.textContent = money(subtotal, cart.currency);
    if (count > 0) checkoutEl.removeAttribute('aria-disabled');
    else checkoutEl.setAttribute('aria-disabled', 'true');

    /* cross-sell: only while the cart has lines; hide items already in the cart */
    if (xBlock) {
      var inCart = {};
      visible.forEach(function (i) { inCart[String(i.product_id)] = true; });
      var shown = 0;
      Array.prototype.forEach.call(xItems, function (x) {
        var hide = !!inCart[x.getAttribute('data-product-id')];
        x.hidden = hide;
        if (!hide) shown++;
      });
      xBlock.hidden = !(count > 0 && shown > 0);
    }

    updateBadges(count);
    document.dispatchEvent(new CustomEvent('cc:cart:updated', { detail: { cart: cart, count: count, lines: visible } }));
    return cart;
  }

  function updateBadges(count) {
    document.querySelectorAll('[data-cc-cart-count]').forEach(function (b) {
      b.textContent = String(count);
      if (!b.hasAttribute('data-cc-cart-count-always')) b.hidden = count === 0;
    });
    document.querySelectorAll('[data-cc-cart-count-dot]').forEach(function (d) { d.hidden = count === 0; });
    document.querySelectorAll('[data-cc-cart-open]').forEach(function (b) {
      if (b.hasAttribute('aria-label')) b.setAttribute('aria-label', 'Open cart, ' + count + ' items');
    });
  }

  /* ---------- actions ---------- */
  function refresh() {
    return enqueue(function () { return getCart().then(render); });
  }

  function change(key, quantity, fromDrawer) {
    return enqueue(function () {
      showError('');
      return req(root_url('/cart/change.js'), { id: key, quantity: quantity }).then(function (cart) {
        var visible = (cart.items || []).filter(function (i) { return !isHidden(i); });
        var markers = (cart.items || []).filter(isHidden);
        if (fromDrawer && visible.length === 0 && markers.length) {
          var updates = {};
          markers.forEach(function (m) { updates[m.key] = 0; });
          return req(root_url('/cart/update.js'), { updates: updates });
        }
        return cart;
      }).then(render);
    });
  }

  /* SHARED RULE (gifts/bundle phase): anything added through CCCart.add is a
     bundle, merch or a gift card, and must never check out as a subscription
     first-order. So after every successful add, any marker line
     (ONETIMEORDER / ORDERGUARANTEE — the same SKU/handle list the drawer hides,
     snippets/cc-cart-markers.liquid) left behind by an abandoned PDP / step-2
     cart is removed. Runs AFTER the add, so a rejected add (sold out) leaves the
     cart untouched. Opt out with CCCart.add(items, { keepMarkers: true }). */
  function stripMarkers(cart) {
    var updates = {}, n = 0;
    (cart.items || []).forEach(function (i) {
      if (isHidden(i)) { updates[i.key] = 0; n++; }
    });
    return n ? req(root_url('/cart/update.js'), { updates: updates }) : cart;
  }

  function add(items, opts) {
    var open = !opts || opts.open !== false;
    var keep = !!(opts && opts.keepMarkers);
    return enqueue(function () {
      showError('');
      return req(root_url('/cart/add.js'), { items: items }).then(getCart).then(function (cart) {
        return keep ? cart : stripMarkers(cart);
      }).then(render);
    }).then(function (cart) {
      if (open) openDrawer(false);
      return cart;
    });
  }

  /* ---------- open / close ---------- */
  function focusables() {
    return Array.prototype.filter.call(
      panel.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null || n === document.activeElement; }
    );
  }
  function openDrawer(doRefresh) {
    if (!state.open) {
      state.open = true;
      state.lastFocus = document.activeElement;
      root.hidden = false;
      document.documentElement.classList.add('cc-cart-open');
      var closeBtn = root.querySelector('.cc-cart__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }
    if (doRefresh === false && state.cart) return Promise.resolve(state.cart);
    return refresh().catch(function (e) { showError(e.message); });
  }
  function closeDrawer() {
    if (!state.open) return;
    state.open = false;
    root.hidden = true;
    document.documentElement.classList.remove('cc-cart-open');
    if (state.lastFocus && state.lastFocus.focus) {
      try { state.lastFocus.focus({ preventScroll: true }); } catch (e) {}
    }
  }

  /* ---------- wiring ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var opener = t.closest('[data-cc-cart-open]');
    if (opener) { e.preventDefault(); openDrawer(); return; }
    if (!root.contains(t)) return;
    if (t.closest('[data-cc-cart-close]')) { e.preventDefault(); closeDrawer(); return; }

    var qb = t.closest('[data-cc-qty]');
    if (qb) {
      var row = qb.closest('.cc-cart__line');
      var key = row && row.getAttribute('data-key');
      var item = state.cart && state.cart.items.filter(function (i) { return i.key === key; })[0];
      if (!item) return;
      row.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
      change(key, Math.max(0, item.quantity + parseInt(qb.getAttribute('data-cc-qty'), 10)), true)
        .catch(function (err) {
          showError(err.message);
          row.querySelectorAll('button').forEach(function (b) { b.disabled = false; });
        });
      return;
    }

    var xb = t.closest('[data-cc-xsell-add]');
    if (xb) {
      xb.disabled = true;
      add([{ id: parseInt(xb.getAttribute('data-cc-xsell-add'), 10), quantity: 1 }], { open: false })
        .catch(function (err) { showError(err.message); })
        .then(function () { xb.disabled = false; });
      return;
    }

    var co = t.closest('[data-cc-cart-checkout]');
    if (co && co.getAttribute('aria-disabled') === 'true') e.preventDefault();
  });

  document.addEventListener('keydown', function (e) {
    if (!state.open) return;
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key === 'Tab') {
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  document.addEventListener('cc:cart:open', function () { openDrawer(); });
  document.addEventListener('cc:cart:close', closeDrawer);
  document.addEventListener('cc:cart:refresh', function () { refresh().catch(function () {}); });

  /* Warm the cart JSON on intent so the first open paints immediately. */
  var warmed = false;
  function warm() { if (!warmed) { warmed = true; refresh().catch(function () { warmed = false; }); } }
  document.querySelectorAll('[data-cc-cart-open]').forEach(function (b) {
    b.addEventListener('pointerenter', warm, { passive: true });
    b.addEventListener('focus', warm, { passive: true });
    b.addEventListener('touchstart', warm, { passive: true });
  });

  /* Back from checkout (bfcache) → the cart may have changed; resync counts.
     On a normal load, sync only when the shopper already has a cart cookie
     (the header's first paint is server-rendered and correct; this guards
     against a cached page), so a cart-less homepage visit makes no request. */
  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh().catch(function () {}); });
  if (/(?:^|;\s*)cart=/.test(document.cookie)) {
    (window.requestIdleCallback || function (cb) { return setTimeout(cb, 1500); })(function () { refresh().catch(function () {}); });
  }

  window.CCCart = {
    add: add,
    open: function () { return openDrawer(); },
    close: closeDrawer,
    refresh: refresh,
    get cart() { return state.cart; }
  };
})();
